<!--
@component
Lineup

Renders both teams' starting XI on a pitch background, arranged by
formation.
-->

<script lang="ts">
  import Flag from '$lib/components/common/Flag/index.svelte';

  interface Player {
    matchName: string;
    playerId?: string;
    [key: string]: unknown;
  }

  // Loose prop types — upstream `matchStats` / team shapes are intentionally
  // permissive (see `src/lib/@types/opta.ts` and `MatchPage/Stats/index.svelte`).
  // We narrow internally with small helpers.
  interface Props {
    matchStats?: unknown;
    homeTeam?: unknown;
    awayTeam?: unknown;
  }

  let { matchStats, homeTeam, awayTeam }: Props = $props();

  const getString = (obj: unknown, key: string): string | undefined => {
    if (obj && typeof obj === 'object' && key in obj) {
      const v = (obj as Record<string, unknown>)[key];
      return typeof v === 'string' ? v : undefined;
    }
    return undefined;
  };

  const getTeamName = (team: unknown): string => getString(team, 'name') ?? '';

  const getTeamStats = (idx: number): Record<string, unknown> | undefined => {
    if (!Array.isArray(matchStats)) return undefined;
    const t = matchStats[idx];
    return t && typeof t === 'object' ?
        (t as Record<string, unknown>)
      : undefined;
  };

  const getPlayers = (team: Record<string, unknown> | undefined): Player[] => {
    const p = team?.player;
    return Array.isArray(p) ? (p as Player[]) : [];
  };

  /** Number of starting players (GK + 10). */
  const STARTERS = 11;
  /** Vertical space, in px, allocated per formation line. */
  const LINE_HEIGHT_PX = 65;

  /** Parse a formation string like `"433"` into `[4, 3, 3]`. */
  const parseFormation = (formation: string | undefined): number[] =>
    (formation ?? '')
      .split('')
      .map((n) => Number.parseInt(n, 10))
      .filter((n) => Number.isFinite(n) && n > 0);

  /**
   * Split the 11 starters into rows according to a formation, e.g.
   * `"433"` → `[[GK], [D, D, D, D], [M, M, M], [F, F, F]]`.
   *
   * Pure: does not mutate the input array.
   *
   * @param reverse - reverse the order of players within each outfield line
   *   so that mirrored halves of the pitch line up visually.
   */
  const makeSquad = (
    players: Player[],
    formation: number[],
    reverse = false
  ): Player[][] => {
    const squad: Player[][] = [];
    let i = 0;

    // Goalkeeper first
    squad.push(players.slice(i, i + 1));
    i += 1;

    for (const size of formation) {
      const line = players.slice(i, i + size);
      squad.push(reverse ? line.slice().reverse() : line);
      i += size;
    }
    return squad;
  };

  const homeStats = $derived(getTeamStats(0));
  const awayStats = $derived(getTeamStats(1));

  const homeFormation = $derived(
    parseFormation(getString(homeStats, 'formationUsed'))
  );
  const awayFormation = $derived(
    parseFormation(getString(awayStats, 'formationUsed'))
  );

  const maxLines = $derived(
    Math.max(homeFormation.length, awayFormation.length)
  );
  const halfSize = $derived(`${(maxLines + 1) * LINE_HEIGHT_PX}px`);

  const homePlayers = $derived(getPlayers(homeStats).slice(0, STARTERS));
  const awayPlayers = $derived(getPlayers(awayStats).slice(0, STARTERS));

  const homeSquad = $derived(makeSquad(homePlayers, homeFormation, true));
  const awaySquad = $derived(makeSquad(awayPlayers, awayFormation).reverse());

  const homeName = $derived(getTeamName(homeTeam));
  const awayName = $derived(getTeamName(awayTeam));

  const hasLineups = $derived(
    homePlayers.length === STARTERS &&
      awayPlayers.length === STARTERS &&
      homeFormation.length > 0 &&
      awayFormation.length > 0
  );
</script>

{#if hasLineups}
  <div class="field" style:--halfSize={halfSize}>
    <div class="flag home">
      <Flag link={false} width="3rem" country={homeName} />
      <div class="formation">{homeFormation.join('-')}</div>
    </div>

    <div class="field-half" aria-label="{homeName} starting lineup">
      {#each homeSquad as line, lineIdx (lineIdx)}
        <div class="position-line">
          {#each line as player, playerIdx (player.playerId ?? `${lineIdx}-${player.matchName}`)}
            <div class="player">
              {player.matchName}
            </div>
          {/each}
        </div>
      {/each}
      <div class="position-line empty" aria-hidden="true"></div>
    </div>

    <svg aria-hidden="true" focusable="false">
      <circle cy="50%" cx="50%" r="9%"></circle>
    </svg>

    <div class="field-half bottom" aria-label="{awayName} starting lineup">
      <div class="position-line empty" aria-hidden="true"></div>
      {#each awaySquad as line, lineIdx (lineIdx)}
        <div class="position-line">
          {#each line as player, playerIdx (player.playerId ?? `${lineIdx}-${player.matchName}`)}
            <div class="player">
              {player.matchName}
            </div>
          {/each}
        </div>
      {/each}
    </div>

    <div class="flag away">
      <div class="formation">{awayFormation.join('-')}</div>
      <Flag link={false} width="3rem" country={awayName} />
    </div>
  </div>
{/if}

<style lang="scss">
  @use '../../../styles/mixins' as mixins;

  svg {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;

    circle {
      stroke: var(--theme-style-pitch-line-faint-colour);
      stroke-width: 1px;
      fill: transparent;
    }
  }

  .flag {
    position: absolute;
    text-align: center;
    left: 10px;

    :global(img) {
      border: 0.5px solid var(--theme-colour-tan-grey);
    }

    &.home {
      top: 10px;
    }
    &.away {
      bottom: 10px;
    }

    .formation {
      color: var(--theme-colour-white);
      @include mixins.text-xs;
      @include mixins.font-medium;
    }
  }

  .field {
    position: relative;
    max-width: 600px;
    margin: 0 auto;
    padding: 5px;
    background-color: var(--theme-colour-pitch-green);

    .field-half {
      border: 1px solid var(--theme-style-pitch-line-colour);
      background-color: var(--theme-style-pitch-surface-colour);
      background-image: url('$images/branding/match-card-texture.svg');
      background-blend-mode: multiply;
      background-repeat: no-repeat;
      background-position: 50% 0%;
      background-size: 250%;
      height: var(--halfSize);
      display: flex;
      flex-wrap: wrap;
      align-content: space-between;

      &.bottom {
        margin-top: -1px;
        background-position: 50% 100%;
        background-image: url('$images/branding/match-card-texture-reverse.svg');
      }

      .position-line {
        margin: 10px 0;
        width: 100%;
        display: flex;
        justify-content: space-evenly;

        &.empty {
          height: 20px;
        }

        .player {
          color: var(--theme-colour-white);
          text-align: center;
          // Truncation requires the overflow/whitespace pair, otherwise
          // text-overflow has no effect.
          max-width: 100%;
          overflow: hidden;
          white-space: nowrap;
          text-overflow: ellipsis;
          @include mixins.fpx-1;
          @include mixins.text-xs;
          @include mixins.font-medium;
          @include mixins.leading-none;
        }
      }
    }
  }
</style>
